import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ContentChart from '../base/content-chart';
import { NAME_PATH, useTralitSetting } from '../../hooks/useTralitSetting';
import { AppContext } from '../../contexts/AppContext';
import { groupBy, isNil, orderBy, sumBy, union } from 'lodash';
import APIList from '../../http/ApiList';
import { Button, Card, Flex, Form, Input, Splitter, Table, Tabs, Tooltip } from 'antd';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { KPI_CODE } from '../../enum/toshiba.enum';
import dayjs from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';
import { useSelectorSize } from '../../hooks/useWindowSize';
import { API_MODE, getS3Object } from '@meteor/frontend-core';
import { convertToJSON } from '../../utils/commonUtil';
import { EVALUATION_PERIOD } from '../../enum/common.enum';
import { useNavigate } from 'react-router-dom';
import { buSortMap, segmentSortSeqMap } from '../../contexts/provider/toshiba.provider';
import { useSnapshot } from '../../hooks/useSnapshot';
import ChangeHeader from './change-header';
import { useSessionStorageState } from 'ahooks';
import ChangeChart from './change-chart';
import PotentialChart from './potential-chart';

type ChartProps = {};

const ChangePage: React.FC<ChartProps> = (props) => {
  const { color } = useContext(AppContext);
  const { kpiValue, kpiUnitLabel, drillFields, setDrillFields, filter, setFilter, getAggregationPeriod } = useContext(ToshibaContext);
  const { currentSnapshot, prefSnapshot, getMonthLabel } = useSnapshot();
  const navigate = useNavigate();
  const [dataProvider, setDataProvider] = useState<any>({
    data: [],
    target: [],
    plan: [],
    prefYear: [],
    reportOverallData: [],
    reportOverallTarget: [],
    potentialDiff: [],
  });
  const [loading, setLoading] = useState(true);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  // form
  const [form] = Form.useForm();

  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams);

  const defaultDrillDownRule = useMemo(() => ['segment', 'businessUnit', 'model', 'customer', 'location'], []);
  const [drillDownRule, setDrillDownRule] = useSessionStorageState<any>('changeDrillDown', {
    defaultValue: defaultDrillDownRule,
  });

  const filterMethod = useCallback(
    () => (item) => {
      return drillFields.every((drillField) => drillField.value.includes(item[drillField.fieldKey]));
    },
    [drillFields, currentSnapshot, filter]
  );
  const gbOrderStatusCur = useMemo(() => {
    const res = groupBy(dataProvider.data.filter(filterMethod()), 'orderStatus');
    if (!res['調整']) {
      res['調整'] = null;
    }
    return res;
  }, [filterMethod, dataProvider]);
  const gbOrderStatusTar = useMemo(() => {
    const res = groupBy(dataProvider.target.filter(filterMethod()), 'orderStatus');
    if (!res['調整']) {
      res['調整'] = null;
    }
    return res;
  }, [filterMethod, dataProvider]);

  const curDrillFieldKey = useMemo(() => {
    const lastDrillField = drillFields.at(-1);
    if (lastDrillField) {
      if (drillDownRule.length === drillDownRule.indexOf(lastDrillField.fieldKey)) {
        return drillDownRule.at(-1);
      }
      return drillDownRule[drillDownRule.indexOf(lastDrillField.fieldKey) + 1];
    } else {
      return drillDownRule[0];
    }
  }, [drillDownRule, drillFields]);

  const drillLabels = {
    segment: 'セグメント',
    businessUnit: 'BU',
    model: '機種',
    customer: '顧客名',
    location: '設置場所名',
  };

  // 获取Table滚动条高度
  const { selectorTop } = useSelectorSize({
    selector: '.tralit-tabs-content-holder',
    watcherSelector: ['.tralit-splitter-panel', '.tralit-table-header', '.summary-kpi-card'],
  });

  const getData = ({ targetSnapshot, fiscalYear, evaluationPeriod }) => {
    if (!currentSnapshot || !targetSnapshot) {
      return;
    }
    setLoading(true);
    // 计算集計期間
    const aggregationPeriod = getAggregationPeriod(currentSnapshot, 'FY25', evaluationPeriod);
    if (aggregationPeriod) {
      const [startDate, endDate] = aggregationPeriod;
      APIList.getChangeData()
        .post({
          apiMode: API_MODE.LONG_RESPONSE,
          currentSnapshot,
          targetSnapshot,
          fiscalYear,
          aggregationPeriod: [startDate.format('YYYYMM'), endDate.format('YYYYMM')],
        })
        .then(async (data) => {
          const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME_TOSHIBA, data.bucketKey);
          setDataProvider({
            data: convertToJSON(response.data),
            target: convertToJSON(response.target),
            plan: convertToJSON(response.plan),
            prefYear: convertToJSON(response.prefYear),
            reportOverallData: convertToJSON(response.reportOverallData),
            reportOverallTarget: convertToJSON(response.reportOverallTarget),
            potentialDiff: convertToJSON(response.potentialDiff),
          });

          const filter = {
            targetSnapshot,
            fiscalYear,
            evaluationPeriod,
            aggregationPeriod,
          };
          setFilter(filter);
          form.setFieldsValue(filter);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentSnapshot) {
      if (drillFields.findIndex((item) => item.fieldKey === 'orderStatus') > 1) {
        const [segment, businessUnit] = drillFields;
        setDrillFields([segment, businessUnit]);
      }
      getData({
        fiscalYear: 'FY25',
        evaluationPeriod: params.evaluationPeriod || filter.evaluationPeriod || EVALUATION_PERIOD.FULL_PERIOD,
        targetSnapshot: filter.targetSnapshot || prefSnapshot,
      });
    }
  }, [currentSnapshot, prefSnapshot]);

  const toolTipFormatter = (params) => {
    return `${params
      .map((param) => {
        if (['placeholder', '总数'].includes(param.seriesName)) {
          return '';
        }
        if (param.name === `比較断面(${getMonthLabel(filter.targetSnapshot)})`) {
          if (!['差分', '前年'].includes(param.seriesName)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value
            )}</span></div>`;
          }
        }
        if (param.name === '差分') {
          if (['diff', 'plan'].includes(param.seriesId)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value
            )}</span></div>`;
          }
        }
        if (param.name === 'ポテンシャル差分') {
          if (['potentialDiff', 'plan'].includes(param.seriesId)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value
            )}</span></div>`;
          }
        }
        if (param.name === `今月断面(${getMonthLabel(currentSnapshot)})`) {
          if (!['差分', '前年'].includes(param.seriesName)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value
            )}</span></div>`;
          }
        }
        if (param.name === '前年同月') {
          if (['前年', '予算'].includes(param.seriesName)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value
            )}</span></div>`;
          }
        }
        return '';
      })
      .join('')}`;
  };

  const columns = useMemo(() => {
    const renderCategory = (text) => {
      return (
        <span
          style={drillDownRule.at(-1) !== curDrillFieldKey ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
          onClick={() => {
            if (drillDownRule.at(-1) !== curDrillFieldKey) {
              setDrillFields((pref) => [...pref, { fieldKey: curDrillFieldKey, value: [text] }]);
            }
          }}
        >
          {text}
        </span>
      );
    };
    const render = (text) =>
      kpiValue(KPI_CODE.SALES_AMOUNT, text, false, {
        [KPI_CODE.SALES_AMOUNT]: {
          unitValue: 1000000,
          unitLabel: '百万',
          precision: 0,
        },
      });
    const renderCurDiff = (text, record) =>
      kpiValue(KPI_CODE.SALES_AMOUNT, record.currentTotal - record.plan, true, {
        [KPI_CODE.SALES_AMOUNT]: {
          unitValue: 1000000,
          unitLabel: '百万',
          precision: 0,
        },
      });
    const renderTarDiff = (text, record) =>
      kpiValue(KPI_CODE.SALES_AMOUNT, record.targetTotal - record.plan, true, {
        [KPI_CODE.SALES_AMOUNT]: {
          unitValue: 1000000,
          unitLabel: '百万',
          precision: 0,
        },
      });
    return [
      {
        key: `category`,
        dataIndex: 'category',
        title: drillLabels[curDrillFieldKey],
        width: 120,
        fixed: 'left',
        render: renderCategory,
      },
      {
        key: `plan`,
        dataIndex: 'plan',
        title: '予算',
        width: 100,
        fixed: 'left',
        className: 'text-right',
        render,
      },
      {
        title: `今月断面(${getMonthLabel(currentSnapshot)})`,
        width: 100,
        fixed: 'left',
        children: [
          {
            key: `currentTotal`,
            dataIndex: `currentTotal`,
            title: '合計',
            className: 'text-right',
            fixed: 'left',
            render,
            width: 100,
          },
          {
            key: `current既受注`,
            dataIndex: `current既受注`,
            title: '①既受注',
            className: 'text-right',
            fixed: 'left',
            width: 90,
            render,
          },
          {
            key: `current未受注`,
            dataIndex: `current未受注`,
            title: '②未受注',
            className: 'text-right',
            fixed: 'left',
            width: 90,
            render,
          },
          {
            key: `current白地`,
            dataIndex: `current白地`,
            title: '③白地',
            className: 'text-right',
            fixed: 'left',
            width: 90,
            render,
          },
          {
            key: `current調整`,
            dataIndex: `current調整`,
            title: '④調整',
            className: 'text-right',
            fixed: 'left',
            width: 90,
            render,
          },
          {
            key: `対予算差分`,
            title: '対予算差分',
            className: 'text-right',
            fixed: 'left',
            width: 110,
            render: renderCurDiff,
          },
          {
            key: `currentPotential`,
            dataIndex: `currentPotential`,
            title: '見込外ポテンシャル',
            className: 'text-right',
            fixed: 'left',
            width: 180,
            render,
          },
        ],
      },
      {
        title: `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
        width: 100,
        children: [
          { key: `targetTotal`, dataIndex: `targetTotal`, title: '合計', className: 'text-right', render, width: 100 },
          {
            key: `target既受注`,
            dataIndex: `target既受注`,
            title: '①既受注',
            className: 'text-right',
            width: 150,
            render,
          },
          {
            key: `target未受注`,
            dataIndex: `target未受注`,
            title: '②未受注',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `target白地`,
            dataIndex: `target白地`,
            title: '③白地',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `target調整`,
            dataIndex: `target調整`,
            title: '④調整',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `対予算差分`,
            title: '対予算差分',
            className: 'text-right',
            width: 110,
            render: renderTarDiff,
          },
          {
            key: `targetPotential`,
            dataIndex: `targetPotential`,
            title: '見込外ポテンシャル',
            className: 'text-right',
            width: 180,
            render,
          },
        ],
      },
      {
        title: '差分',
        width: 100,
        children: [
          { key: `diffTotal`, dataIndex: `diffTotal`, title: '合計', className: 'text-right', render, width: 100 },
          {
            key: `diff既受注`,
            dataIndex: `diff既受注`,
            title: '①既受注',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `diff未受注`,
            dataIndex: `diff未受注`,
            title: '②未受注',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `diff白地`,
            dataIndex: `diff白地`,
            title: '③白地',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `diff調整`,
            dataIndex: `diff調整`,
            title: '④調整',
            className: 'text-right',
            width: 90,
            render,
          },
          {
            key: `diffPotential`,
            dataIndex: `diffPotential`,
            title: '見込外ポテンシャル',
            className: 'text-right',
            width: 180,
            render,
          },
        ],
      },
    ];
  }, [gbOrderStatusCur, gbOrderStatusTar, curDrillFieldKey]);

  const potentialDataSource = useMemo(() => {
    const { potentialDiff } = dataProvider;
    const tempData = groupBy(potentialDiff, 'segment');
    return tempData;
  }, [dataProvider]);

  const searchInput = useRef(null);
  const getColumnSearchProps = useCallback((dataIndex: string, title: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, close }) => (
        <div style={{ padding: 8, width: 320 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            style={{ width: '100%' }}
            allowClear
            ref={searchInput}
            placeholder={`${title}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
            }}
          />
          <Flex style={{ marginTop: 6 }} justify="flex-end">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                confirm();
              }}
            >
              Search
            </Button>
          </Flex>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? color.primaryColor : undefined }} />,
      filterDropdownProps: {
        onOpenChange(open) {
          if (open) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
      },
      onFilter: (value, record) => {
        return record[dataIndex].includes(value);
      },
    };
  }, []);

  const [activeKey, setActiveKey] = useState('1');
  const potentialColumns: any[] = useMemo(() => {
    const s = activeKey.split('_');
    let seg = null;
    if (s.length === 2) {
      seg = s[0];
    }
    if (!seg) {
      return [];
    }
    const dataS = potentialDataSource[seg];
    const buFilters = dataS.reduce((pr, pt) => (pr.includes(pt.businessUnit) ? pr : pr.concat(pt.businessUnit)), []);
    const sDepartFilters = dataS.reduce((pr, pt) => (pr.includes(pt.salesDepartment) ? pr : pr.concat(pt.salesDepartment)), []);
    const sSectionFilters = dataS.reduce((pr, pt) => (pr.includes(pt.salesSection) ? pr : pr.concat(pt.salesSection)), []);
    const probFilters = dataS.reduce((pr, pt) => (pr.includes(pt.probability) ? pr : pr.concat(pt.probability)), []);
    const actionFilters = dataS.reduce((pr, pt) => (pr.includes(pt.action) ? pr : pr.concat(pt.action)), []);
    const harvestPeriods = dataS
      .reduce((pr, pt) => {
        if (pt.harvestPeriod) {
          const harvestPeriods = pt.harvestPeriod.split(',').map((v) => v.trim());
          harvestPeriods.forEach((v) => {
            if (!pr.includes(v)) {
              pr.push(v);
            }
          });
        }
        return pr;
      }, [])
      .sort();
    const render = (val) => (
      <Tooltip
        title={<div style={{ fontSize: fontSize.table }}>{val}</div>}
        getPopupContainer={(triggerNode: any) => triggerNode.parentNode}
      >
        <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
      </Tooltip>
    );
    const saleAmountRender = (val) => (
      <div style={{ textAlign: 'right' }}>
        {kpiValue(KPI_CODE.SALES_AMOUNT, val, false, {
          [KPI_CODE.SALES_AMOUNT]: {
            unitValue: 1000000,
            unitLabel: '百万',
            precision: 0,
          },
        })}
      </div>
    );
    const saleAmountDiffRender = (val) => (
      <div style={{ textAlign: 'right' }}>
        {kpiValue(KPI_CODE.SALES_AMOUNT, val, true, {
          [KPI_CODE.SALES_AMOUNT]: {
            unitValue: 1000000,
            unitLabel: '百万',
            precision: 0,
          },
        })}
      </div>
    );

    return [
      {
        key: `segment`,
        dataIndex: 'segment',
        title: 'セグメント',
        width: 100,
        render,
      },
      {
        key: `businessUnit`,
        dataIndex: 'businessUnit',
        title: 'BU',
        width: 120,
        render,
        filters: buFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.businessUnit === value;
        },
      },
      {
        key: `salesDepartment`,
        dataIndex: 'salesDepartment',
        title: '営業部',
        width: 100,
        render,
        filters: sDepartFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.salesDepartment === value;
        },
      },
      {
        key: `salesSection`,
        dataIndex: 'salesSection',
        title: '営業課',
        width: 100,
        render,
        filters: sSectionFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.salesSection === value;
        },
      },
      {
        key: `projectName`,
        dataIndex: 'projectName',
        title: '案件名',
        width: 340,
        render,
        ...getColumnSearchProps('projectName', '案件名'),
      },

      {
        key: `cur`,
        dataIndex: 'cur',
        sorter: (a, b) => a.cur - b.cur,
        defaultSortOrder: 'descend',
        title: `売上`,
        width: 80,
        render: saleAmountRender,
      },
      {
        key: `prev`,
        dataIndex: 'prev',
        sorter: (a, b) => a.prev - b.prev,
        title: `前回売上`,
        width: 80,
        render: saleAmountRender,
      },
      {
        key: `diff`,
        dataIndex: 'diff',
        sorter: (a, b) => a.diff - b.diff,
        title: `差分`,
        width: 80,
        render: saleAmountDiffRender,
      },
      {
        key: `probability`,
        dataIndex: 'probability',
        sorter: (a, b) => {
          const order = ['高', '中', '低'];
          if (!a.probability && b.probability) return 1;
          if (a.probability && !b.probability) return -1;
          return order.indexOf(a.probability) - order.indexOf(b.probability);
        },
        title: '確度',
        width: 80,
        render,
        filters: probFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.probability === value;
        },
      },
      {
        key: `action`,
        dataIndex: 'action',
        title: '施策',
        width: 120,
        render,
        filters: actionFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.action === value;
        },
      },
      {
        key: `harvestPeriod`,
        dataIndex: 'harvestPeriod',
        title: '刈取時期',
        width: 120,
        render,
        filters: harvestPeriods.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          return record.harvestPeriod.includes(value);
        },
      },
    ];
  }, [potentialDataSource, activeKey]);

  const dataSource = useMemo(() => {
    const { data: orgData, target: orgTarget, plan: orgPlan, reportOverallData, potentialDiff: orgPotentialDiff } = dataProvider;
    const [overallData] = reportOverallData;
    const data = orgData.filter(filterMethod());
    const target = orgTarget.filter(filterMethod());
    const plan = orgPlan.filter(filterMethod());
    const potentialDiff = orgPotentialDiff.filter(filterMethod());

    const gbData: any = groupBy(data, curDrillFieldKey);
    const gbTarget: any = groupBy(target, curDrillFieldKey);
    const gbPlan: any = groupBy(plan, curDrillFieldKey);
    let dataValues = curDrillFieldKey === 'segment' ? [...Object.keys(gbData), '調整'] : Object.keys(gbData);
    switch (curDrillFieldKey) {
      case 'segment':
        dataValues.sort((a, b) => segmentSortSeqMap[a] - segmentSortSeqMap[b]);
        break;
      case 'businessUnit':
        const map = buSortMap[drillFields[0].value];
        if (drillFields[0].value[0] === '原子力') {
          dataValues = dataValues.filter((dv) => dv !== '国内');
        }
        dataValues.sort((a, b) => map[a] - map[b]);
        break;
      default:
        break;
    }

    return orderBy(
      [
        ...dataValues.map((categoryValue) => {
          if (curDrillFieldKey === 'segment' && categoryValue === '調整') {
            const adjustCurMap = {
              [EVALUATION_PERIOD.ACTUAL_ONLY]: overallData?.salesActuals - sumBy(data, KPI_CODE.SALES_AMOUNT),
              [EVALUATION_PERIOD.FORECAST_ONLY]: overallData?.salesForecast - sumBy(data, KPI_CODE.SALES_AMOUNT),
              default: overallData?.salesActuals + overallData?.salesForecast - sumBy(data, KPI_CODE.SALES_AMOUNT),
            };
            const adjustTarMap = {
              [EVALUATION_PERIOD.ACTUAL_ONLY]: overallData?.salesActuals - sumBy(target, KPI_CODE.SALES_AMOUNT),
              [EVALUATION_PERIOD.FORECAST_ONLY]: overallData?.salesForecast - sumBy(target, KPI_CODE.SALES_AMOUNT),
              default: overallData?.salesActuals + overallData?.salesForecast - sumBy(target, KPI_CODE.SALES_AMOUNT),
            };
            const adjustDiffMap = {
              [EVALUATION_PERIOD.ACTUAL_ONLY]:
                overallData?.salesActuals -
                sumBy(data, KPI_CODE.SALES_AMOUNT) -
                (overallData?.salesActuals - sumBy(target, KPI_CODE.SALES_AMOUNT)),
              [EVALUATION_PERIOD.FORECAST_ONLY]:
                overallData?.salesForecast -
                sumBy(data, KPI_CODE.SALES_AMOUNT) -
                (overallData?.salesForecast - sumBy(target, KPI_CODE.SALES_AMOUNT)),
              default:
                overallData?.salesActuals +
                overallData?.salesForecast -
                sumBy(data, KPI_CODE.SALES_AMOUNT) -
                (overallData?.salesActuals + overallData?.salesForecast - sumBy(target, KPI_CODE.SALES_AMOUNT)),
            };
            const adjustCurrent = !isNil(adjustCurMap[filter.evaluationPeriod])
              ? adjustCurMap[filter.evaluationPeriod]
              : adjustCurMap.default;
            const adjustTarget = !isNil(adjustTarMap[filter.evaluationPeriod])
              ? adjustTarMap[filter.evaluationPeriod]
              : adjustTarMap.default;
            const adjustDiff = !isNil(adjustDiffMap[filter.evaluationPeriod])
              ? adjustDiffMap[filter.evaluationPeriod]
              : adjustDiffMap.default;
            return {
              category: categoryValue,
              plan: overallData?.salesPlanForecast - sumBy(plan, KPI_CODE.SALES_AMOUNT),
              current調整: adjustCurrent,
              target調整: adjustTarget,
              diff調整: adjustDiff,
            };
          }
          return {
            category: categoryValue,
            plan: sumBy(gbPlan[categoryValue], KPI_CODE.SALES_AMOUNT),
            currentTotal: sumBy(gbData[categoryValue], KPI_CODE.SALES_AMOUNT),
            targetTotal: sumBy(gbTarget[categoryValue], KPI_CODE.SALES_AMOUNT),
            diffTotal: sumBy(gbData[categoryValue], KPI_CODE.SALES_AMOUNT) - sumBy(gbTarget[categoryValue], KPI_CODE.SALES_AMOUNT),
            currentPotential: sumBy(
              potentialDiff.filter((item) => item[curDrillFieldKey] === categoryValue),
              'cur'
            ),
            targetPotential: sumBy(
              potentialDiff.filter((item) => item[curDrillFieldKey] === categoryValue),
              'prev'
            ),
            diffPotential: sumBy(
              potentialDiff.filter((item) => item[curDrillFieldKey] === categoryValue),
              'diff'
            ),
            ...Object.keys(gbOrderStatusCur).reduce((org, orderStatus) => {
              const categoryData = gbData[categoryValue] || [];
              org[`current${orderStatus}`] = sumBy(
                categoryData.filter((item) => item.orderStatus === orderStatus),
                KPI_CODE.SALES_AMOUNT
              );
              return org;
            }, {}),
            ...Object.keys(gbOrderStatusTar).reduce((org, orderStatus) => {
              const categoryData = gbTarget[categoryValue] || [];
              org[`target${orderStatus}`] = sumBy(
                categoryData.filter((item) => item.orderStatus === orderStatus),
                KPI_CODE.SALES_AMOUNT
              );
              return org;
            }, {}),
            ...Object.keys(gbOrderStatusCur).reduce((org, orderStatus) => {
              const categoryData = gbData[categoryValue] || [];
              const categoryTarget = gbTarget[categoryValue] || [];
              const current = sumBy(
                categoryData.filter((item) => item.orderStatus === orderStatus),
                KPI_CODE.SALES_AMOUNT
              );
              const target = sumBy(
                categoryTarget.filter((item) => item.orderStatus === orderStatus),
                KPI_CODE.SALES_AMOUNT
              );
              org[`diff${orderStatus}`] = current - target;
              return org;
            }, {}),
          };
        }),
      ],
      [
        (item: any) => {
          if (item.category === '調整') {
            return -Infinity;
          }
          return item.currentTotal;
        },
      ],
      ['desc']
    );
  }, [filter, curDrillFieldKey, gbOrderStatusCur, gbOrderStatusTar, dataProvider]);

  const tabItems = useMemo(
    () => [
      {
        key: '受注変動詳細',
        // label: '受注変動詳細',
        label: <span style={{ fontSize: fontSize.tag }}>受注変動詳細</span>,
        children: (
          <Card
            styles={{
              body: { padding: 0 },
              header: { color: '#555', fontWeight: 'bold', fontSize: fontSize.tableTitle, padding: '0 12px' },
            }}
            classNames={{ header: 'list-card-title' }}
          >
            <Table
              // virtual
              size="small"
              className="small-header"
              bordered
              rowHoverable={false}
              rowKey={'category'}
              columns={columns as any[]}
              dataSource={dataSource}
              pagination={false}
              scroll={{
                x: 'max-content',
                y: document.body.clientHeight - selectorTop - 90 - 45,
              }}
              loading={loading}
            />
          </Card>
        ),
      },
      ...Object.entries<any>(potentialDataSource)
        .sort((a, b) => segmentSortSeqMap[a[0]] - segmentSortSeqMap[b[0]])
        .map(([k, v]) => ({
          key: `${k}_ポテンシャルリスト`,
          label: <span style={{ fontSize: fontSize.tag }}>{`${k}_ポテンシャルリスト`}</span>,
          children: (
            <Card
              styles={{
                body: { padding: 0 },
                header: { color: '#555', fontWeight: 'bold', fontSize: fontSize.tableTitle, padding: '0 12px' },
              }}
              classNames={{ header: 'list-card-title' }}
            >
              <Table
                // virtual
                size="small"
                className="small-header"
                bordered
                rowHoverable={false}
                rowKey={(record) =>
                  Object.keys(record)
                    .map((key) => record[key])
                    .join('_')
                }
                columns={potentialColumns}
                dataSource={v}
                pagination={false}
                scroll={{
                  // x: 'max-content',
                  y: document.body.clientHeight - selectorTop - 45 - 45,
                }}
                expandable={{
                  columnWidth: 30,
                  expandedRowRender: (record: any) => <p style={{ margin: 0 }}>{record.memo}</p>,
                  rowExpandable: (record: any) => record.memo,
                }}
                loading={loading}
              />
            </Card>
          ),
        })),
    ],
    [potentialDataSource, selectorTop, columns, dataSource, loading, potentialColumns, fontSize]
  );

  return (
    <>
      <ChangeHeader
        submit={getData}
        form={form}
        loading={loading}
        drillDownRule={drillDownRule}
        setDrillDownRule={setDrillDownRule}
        defaultDrillDownRule={defaultDrillDownRule}
      />
      <Splitter layout="vertical" style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
        <Splitter.Panel
          resizable={false}
          collapsible={{ start: true, end: true, showCollapsibleIcon: true }}
          defaultSize={document.body.clientHeight / 3 + 22}
        >
          <Splitter>
            <Splitter.Panel resizable={false} collapsible={{ start: true, end: true, showCollapsibleIcon: true }} defaultSize={'50%'}>
              <ChangeChart dataProvider={dataProvider} loading={loading} />
            </Splitter.Panel>
            <Splitter.Panel resizable={false} collapsible={{ start: true, end: true, showCollapsibleIcon: true }} defaultSize={'50%'}>
              <PotentialChart dataProvider={dataProvider} loading={loading} curDrillFieldKey={curDrillFieldKey} />
            </Splitter.Panel>
          </Splitter>
        </Splitter.Panel>
        <Splitter.Panel resizable={false}>
          <Tabs
            onChange={(ak) => {
              setActiveKey(ak);
            }}
            type="card"
            defaultActiveKey="1"
            items={tabItems}
            tabBarStyle={{ marginBottom: 0 }}
            tabBarExtraContent={
              <div style={{ fontSize: fontSize.tag, marginRight: 14 }}>
                単位：
                {kpiUnitLabel(
                  KPI_CODE.SALES_AMOUNT,
                  {
                    [KPI_CODE.SALES_AMOUNT]: {
                      unitValue: 1000000,
                      unitLabel: '百万円',
                    },
                  },
                  false
                )}
              </div>
            }
          />
        </Splitter.Panel>
      </Splitter>
    </>
  );
};

export default ChangePage;
